import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Modal, Text, View, Title, StyledModalHandle, StyledModal } from "@constants/Themed";
import moment from 'moment';
import Button from '@components/UI/Button/Button';
import FlipCard from 'react-native-flip-card';
import { FlatList } from 'react-native';
import styles from '@stylesheet';
import { CalendarProvider, Timeline } from 'react-native-calendars';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import CalendarSchema from '../Constants/CalendarSchema';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import { XStack } from 'tamagui';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';

export interface CalendarDayModalRef {
  openModal: () => void;
  closeModal: () => void;
  animateView: () => void;
  openAnimated: () => Promise<void>;
}

type CalendarDayModalProps = {
  selectedDate: Date | string;
  events: any[];
  handleImportCalendar?: (calendars: any) => void;
  callBackFunction?: () => void;
  nullify?: any[];
}

const CalendarDayModal = forwardRef<StyledModalHandle, CalendarDayModalProps>((props, ref) => {
  const { selectedDate, events } = props;
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const theme = themeSelector();
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);

  const handleClose = () => {
    if (ref && typeof ref !== 'function' && ref.current) {
      ref.current.closeModal?.();
    }
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    openModal: () => setOpen(true),
    closeModal: () => setOpen(false),
    animateView: () => { },
    openAnimated: async () => setOpen(true),
  }));

  const { status, data, error, isFetching, isLoading } = useQuery({
    queryKey: ['calendarEntrySchema'],
    queryFn: async () => {
      const data = await apis.getCalendarSchema()
        .then(res => {
          return res
        });
      return data.schema
    },
    staleTime: 1000 * 60 * 60 * 10,
  })

  return (
    <StyledModal
      caption='Add an event to your calendar'
      title={"Adding an event on" + " " + moment(props.selectedDate).format(globalDate).toString()}
      ref={ref}
      isVisible={open}
      extraChildren={(
        <XStack gap={16} paddingHorizontal={20}>
          <Button
            customStyle={{
              flex: 1,
              top: 24,
              minWidth: 120,
              minHeight: 44,
              borderRadius: 16,
              backgroundColor: theme ? 'rgba(90, 192, 234, 0.9)' : 'rgba(70, 70, 70, 0.9)',
              shadowRadius: 8,
              shadowOpacity: theme ? 0.15 : 0.25,
              shadowColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.4)',
              shadowOffset: { width: 0, height: 2 },
              borderWidth: 1,
              borderColor: theme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
            }}
            defaultColor={theme ? 'rgba(90, 192, 234, 0.9)' : 'rgba(70, 70, 70, 0.9)'}
            buttonText={'Timeline'}
            onPress={() => setFlip(false)}
          />
          <Button
            customStyle={{
              flex: 1,
              top: 24,
              minWidth: 120,
              minHeight: 44,
              borderRadius: 16,
              backgroundColor: theme ? 'rgba(220, 53, 69, 0.9)' : 'rgba(180, 50, 50, 0.9)',
              shadowRadius: 8,
              shadowOpacity: theme ? 0.15 : 0.25,
              shadowColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.4)',
              shadowOffset: { width: 0, height: 2 },
              borderWidth: 1,
              borderColor: theme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
            }}
            defaultColor={theme ? 'rgba(220, 53, 69, 0.9)' : 'rgba(180, 50, 50, 0.9)'}
            buttonText={'Close'}
            onPress={handleClose}
          />
        </XStack>
      )}
    >
      <View style={{ flex: 1, overflow: "hidden" }}>
        <FlipCard style={{ flex: 1 }} flipHorizontal={true} flipVertical={false} flip={flip} clickable={false}>
          <View style={[styles.face, {
            flex: 1,
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)',
            borderRadius: 16,
            margin: 8,
            overflow: 'hidden',
          }]}>
            <View style={{ flex: 10, maxWidth: 800 }}>
              <CalendarProvider date={selectedDate?.toString()}>
                <Timeline
                  date={selectedDate?.toString()}
                  events={events.map((item) => ({
                    ...item,
                    start: item.startDate,
                    end: item.endDate,
                  }))}
                  theme={{
                    timeLabel: {
                      fontFamily: 'montserrat',
                      padding: 8,
                      backgroundColor: theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(40, 40, 40, 0.9)',
                      color: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: "600",
                      left: 0,
                      borderWidth: 1,
                      borderColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                      shadowRadius: 4,
                      shadowOpacity: theme ? 0.1 : 0.2,
                    },
                    eventTimes: {
                      fontStyle: 'italic',
                      fontWeight: '500',
                      color: theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                    },
                    contentStyle: {
                      backgroundColor: 'transparent',
                      paddingLeft: 70
                    },
                    timelineContainer: {
                      backgroundColor: 'transparent'
                    }
                  }}
                />
              </CalendarProvider>
            </View>
            <Button
              customStyle={{
                minHeight: 44,
                borderRadius: 16,
                backgroundColor: theme ? 'rgba(40, 167, 69, 0.9)' : 'rgba(60, 140, 60, 0.9)',
                shadowRadius: 8,
                shadowOpacity: theme ? 0.15 : 0.25,
                shadowColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.4)',
                shadowOffset: { width: 0, height: 2 },
                borderWidth: 1,
                borderColor: theme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              }}
              buttonText={'Add Event'}
              onPress={() => setFlip(true)}
            />
          </View>
          <View style={[styles.back, {
            flex: 1,
            paddingVertical: 25,
            backgroundColor: theme ? 'rgba(248, 250, 252, 0.95)' : 'rgba(30, 30, 30, 0.95)',
            borderRadius: 16,
            margin: 8,
          }]}>
            <View style={{ flex: 10 }}>
              <GenerateSchemaForm scrollEnabled schema={data} submitFunction={apis.addCalendarEntry} nullify={props?.nullify} avoidKeyboardDisabled addOffset={120} noBackground buttonLabel='Add Event' extraFunction={props?.callBackFunction} buttonStyle={{ marginBottom: 20 }} />
            </View>
          </View>
        </FlipCard >
      </View>
    </StyledModal >
  )
})
export default CalendarDayModal;